<template>
  <button
    ref="trigger"
    type="button"
    class="rm-trigger-picker"
    aria-haspopup="dialog"
    :aria-expanded="open"
    :aria-label="ariaLabel"
    @click="toggle">
    <span class="min-w-0 flex-1 truncate text-left">
      <slot name="value" :value="modelValue" :placeholder="placeholder">{{ modelValue ?? placeholder }}</slot>
    </span>
    <ChevronDownIcon class="size-4 shrink-0 text-muted" aria-hidden="true" />
  </button>
  <Popover ref="popover" :pt="{ content: { class: 'p-0!' } }" @show="onShow" @hide="open = false">
    <!-- Escape closes only the picker; letting it reach the document would also close a surrounding drawer. -->
    <div
      class="flex w-[46rem] max-w-[calc(100vw-2rem)] flex-col"
      :style="{ height: `${panelHeight}px` }"
      @keydown.esc.stop.prevent="close">
      <div class="flex h-12 shrink-0 items-center gap-2.5 border-b border-line px-4">
        <MagnifyingGlassIcon class="size-4 shrink-0 text-muted" aria-hidden="true" />
        <input
          ref="search"
          v-model="query"
          role="combobox"
          aria-label="Search triggers"
          aria-autocomplete="list"
          :aria-controls="`${uid}-results`"
          aria-expanded="true"
          :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : undefined"
          class="h-full w-full border-0 bg-transparent! p-0 text-[15px] text-heading shadow-none! outline-none placeholder:text-faint focus:ring-0"
          placeholder="Search all triggers…"
          autocomplete="off"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="chooseActive" />
      </div>
      <div class="flex min-h-0 flex-1">
        <div
          v-if="!searching"
          ref="tablist"
          class="w-56 shrink-0 space-y-0.5 overflow-y-auto border-r border-line p-1.5"
          role="tablist"
          aria-orientation="vertical"
          aria-label="Trigger categories">
          <button
            v-for="(group, index) in groups"
            :key="group.name"
            type="button"
            role="tab"
            class="flex h-[34px] w-full items-center justify-between gap-2 rounded-lg px-3 text-left text-[13.5px] hover:bg-overlay"
            :class="group.name === activeGroup?.name ? 'bg-overlay font-semibold text-heading' : 'text-copy'"
            :aria-selected="group.name === activeGroup?.name"
            :aria-controls="`${uid}-results`"
            :tabindex="group.name === activeGroup?.name ? 0 : -1"
            @click="selectGroup(group.name)"
            @mouseenter="selectGroup(group.name)"
            @keydown.down.prevent="focusTab(index + 1)"
            @keydown.up.prevent="focusTab(index - 1)">
            <span class="truncate">{{ group.group }}</span>
            <span class="shrink-0 text-xs font-normal text-muted">{{ group.options.length }}</span>
          </button>
        </div>
        <div
          :id="`${uid}-results`"
          class="min-w-0 flex-1 overflow-y-auto px-2 py-1.5"
          role="listbox"
          :aria-label="searching ? 'Matching triggers' : activeGroup?.group">
          <div v-for="section in sections" :key="section.name" role="group" :aria-label="section.label">
            <h3 v-if="searching" class="px-3 pt-2.5 pb-1 text-[11.5px] font-semibold text-muted">{{
              section.label
            }}</h3>
            <div
              v-for="item in section.items"
              :id="optionId(item.index)"
              :key="item.value"
              role="option"
              :aria-selected="item.index === activeIndex"
              class="flex h-[38px] cursor-pointer items-center gap-3 rounded-lg px-3"
              :class="{ 'bg-overlay': item.index === activeIndex }"
              @mousemove="activeIndex = item.index"
              @mousedown.prevent
              @click="choose(item.value)">
              <span
                v-if="section.hasIcons"
                class="flex size-[22px] shrink-0 items-center justify-center rounded-md bg-overlay text-copy"
                aria-hidden="true">
                <component :is="item.icon" v-if="item.icon" class="size-3.5" />
              </span>
              <span class="min-w-0 truncate text-[13.5px] font-medium text-heading">{{ item.label }}</span>
              <span class="min-w-0 flex-1 truncate text-[13px] text-muted">{{ item.typeLabel }}</span>
              <CheckIcon v-if="item.value === modelValue" class="size-4 shrink-0 text-primary" aria-label="Current" />
            </div>
          </div>
          <p v-if="!flatItems.length" class="px-3 py-5 text-[13px] text-muted">
            {{ searching ? `No triggers match “${query.trim()}”` : 'Nothing to choose here yet' }}
          </p>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useId, useTemplateRef, watch, type Component } from 'vue'
import Popover from 'primevue/popover'
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import type { TriggerGroup, TriggerOption } from '../data/logicDefinitionSchema'
import { questionTypeIcon } from './question/questionCard'

const props = defineProps<{
  modelValue: string | null
  groups: TriggerGroup[]
  placeholder?: string
  ariaLabel?: string
}>()
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

interface PickerItem {
  index: number
  value: string
  label: string
  typeLabel: string
  icon: Component | null
}
interface PickerSection {
  name: string
  label: string
  hasIcons: boolean
  items: PickerItem[]
}

const uid = useId()
const popover = useTemplateRef<InstanceType<typeof Popover>>('popover')
const trigger = useTemplateRef<HTMLButtonElement>('trigger')
const search = useTemplateRef<HTMLInputElement>('search')
const tablist = useTemplateRef<HTMLElement>('tablist')
const open = ref(false)
const query = ref('')
const activeName = ref<string | null>(null)
const activeIndex = ref(-1)
const panelHeight = ref(448)

const activeGroup = computed(() => props.groups.find((group) => group.name === activeName.value) ?? props.groups[0])
const searching = computed(() => query.value.trim() !== '')

// Questions show their type; segment groups that aren't questions get the Segments icon so every row in that
// category carries one.
function describe(option: TriggerOption, group: TriggerGroup) {
  if (option.questionType) {
    return { icon: questionTypeIcon(option.questionType), typeLabel: option.questionType.label }
  }
  return group.name === 'segments'
    ? { icon: shellIcons.segments, typeLabel: 'Segment group' }
    : { icon: null, typeLabel: option.description ?? '' }
}

/**
 * Browsing shows the open category. Searching ranks every category's triggers like the command palette: each word
 * must appear in the trigger, its type or its category, and the matches stay grouped under their category.
 */
const sections = computed<PickerSection[]>(() => {
  const words = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const groups = searching.value ? props.groups : activeGroup.value ? [activeGroup.value] : []
  let index = 0
  return groups.flatMap((group) => {
    const items = group.options.flatMap((option) => {
      const { icon, typeLabel } = describe(option, group)
      const haystack = `${option.label} ${typeLabel} ${group.group}`.toLowerCase()
      return words.every((word) => haystack.includes(word))
        ? [{ index: 0, value: option.value, label: option.label, typeLabel, icon }]
        : []
    })
    if (!items.length && searching.value) {
      return []
    }
    for (const item of items) {
      item.index = index++
    }
    return [{ name: group.name, label: group.group, hasIcons: items.some((item) => item.icon), items }]
  })
})
const flatItems = computed(() => sections.value.flatMap((section) => section.items))

// Highlight the saved trigger when browsing its category, else the first result, and keep it in view.
function syncActive() {
  const items = flatItems.value
  const current = searching.value ? -1 : items.findIndex((item) => item.value === props.modelValue)
  activeIndex.value = current >= 0 ? current : items.length ? 0 : -1
  void nextTick(scrollActiveIntoView)
}
watch(flatItems, syncActive)

function optionId(index: number) {
  return `${uid}-option-${index}`
}

function scrollActiveIntoView() {
  document.getElementById(optionId(activeIndex.value))?.scrollIntoView({ block: 'nearest' })
}

/**
 * Opening starts on the saved trigger's category, else the first, with an empty search. The panel takes at most
 * 448px, shrinking to the larger space above or below the trigger so it's never cut off.
 */
function toggle(event: MouseEvent) {
  if (!open.value) {
    query.value = ''
    activeName.value =
      props.groups.find((group) => group.options.some((option) => option.value === props.modelValue))?.name ??
      props.groups[0]?.name ??
      null
    syncActive()
    const rect = trigger.value?.getBoundingClientRect()
    if (rect) {
      const room = Math.max(window.innerHeight - rect.bottom, rect.top) - 24
      panelHeight.value = Math.min(448, Math.max(240, room))
    }
  }
  open.value = !open.value
  popover.value?.toggle(event, trigger.value ?? undefined)
}

function onShow() {
  scrollActiveIntoView()
  search.value?.focus()
}

function selectGroup(name: string) {
  activeName.value = name
}

function focusTab(index: number) {
  const group = props.groups[(index + props.groups.length) % props.groups.length]
  if (group) {
    selectGroup(group.name)
    void nextTick(() => tablist.value?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus())
  }
}

function move(delta: number) {
  const count = flatItems.value.length
  if (count) {
    activeIndex.value = (activeIndex.value + delta + count) % count
    void nextTick(scrollActiveIntoView)
  }
}

function chooseActive() {
  const item = flatItems.value[activeIndex.value]
  if (item) {
    choose(item.value)
  }
}

function close() {
  popover.value?.hide()
  trigger.value?.focus()
}

function choose(value: string) {
  emit('update:modelValue', value)
  close()
}
</script>

<style scoped>
.rm-trigger-picker {
  display: inline-flex;
  min-width: 12rem;
  max-width: 100%;
  align-items: center;
  gap: 0.5rem;
  padding: var(--p-form-field-padding-y) var(--p-form-field-padding-x);
  border: 1px solid var(--p-form-field-border-color);
  border-radius: var(--p-form-field-border-radius);
  background: var(--p-form-field-background);
  color: var(--p-form-field-color);
  line-height: 1.25;
  cursor: pointer;
  transition: border-color 0.15s;
}
.rm-trigger-picker:hover {
  border-color: var(--p-form-field-hover-border-color);
}
.rm-trigger-picker:focus-visible,
.rm-trigger-picker[aria-expanded='true'] {
  border-color: var(--p-form-field-focus-border-color);
  outline: none;
}
</style>
